import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type CounterAggregate, countersProvider, type Counter } from "@/providers/counters.provider";
import { usersProvider } from "@/providers/users.provider";
import { IconSearch, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

type Tab = "global" | "user" | "aggregate";

export function CountersPage() {
  const [tab, setTab] = useState<Tab>("global");

  // Per-tab filter states kept separate so switching tabs doesn't cross-contaminate
  const [globalFilter, setGlobalFilter] = useState("");
  const [userInput, setUserInput] = useState("");
  const [userId, setUserId] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [aggregatePattern, setAggregatePattern] = useState("");

  // Global counters
  const { data: globalCounters, isLoading: globalLoading, isError: globalError, error: globalErr } = useQuery({
    queryKey: ["global-counters"],
    queryFn: () => countersProvider.getGlobalCounters(),
    staleTime: 30_000,
  });

  // All known counter names — sidebar
  const { data: counterNames, isLoading: namesLoading } = useQuery<string[]>({
    queryKey: ["counter-names"],
    queryFn: () => countersProvider.getCounterNames(),
    staleTime: 60_000,
  });

  // User info for display
  const { data: user } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => usersProvider.getById(userId),
    enabled: !!userId,
    staleTime: 60_000,
  });

  // Per-user counters
  const {
    data: userCounters,
    isLoading: countersLoading,
    isError: countersError,
    error: countersErr,
  } = useQuery<Counter[]>({
    queryKey: ["user-counters", userId],
    queryFn: () => countersProvider.getUserCounters(userId),
    enabled: !!userId,
    staleTime: 30_000,
  });

  // Aggregate counters
  const {
    data: aggregateCounters,
    isLoading: aggregateLoading,
    isError: aggregateError,
    error: aggregateErr,
  } = useQuery<CounterAggregate[]>({
    queryKey: ["aggregate-counters", aggregatePattern],
    queryFn: () => countersProvider.getAggregateCounters(aggregatePattern),
    enabled: tab === "aggregate",
    staleTime: 30_000,
  });

  function handleUserSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = userInput.trim();
    if (trimmed) setUserId(trimmed);
  }

  // Filtered rows
  const filteredGlobal = (globalCounters ?? []).filter((c) =>
    globalFilter ? c.counter_name.toLowerCase().includes(globalFilter.toLowerCase()) : true
  );

  const filteredUserCounters = (userCounters ?? []).filter((c) =>
    userFilter ? c.counter_name.toLowerCase().includes(userFilter.toLowerCase()) : true
  );

  // Sidebar: show names relevant to active tab
  const activeFilter = tab === "global" ? globalFilter : tab === "user" ? userFilter : aggregatePattern;
  const sidebarNames = activeFilter
    ? (counterNames ?? []).filter((n) => n.toLowerCase().includes(activeFilter.toLowerCase()))
    : (counterNames ?? []);

  function sidebarValue(name: string) {
    if (tab === "user") return userCounters?.find((c) => c.counter_name === name)?.value;
    if (tab === "aggregate") return aggregateCounters?.find((c) => c.counter_name === name)?.total;
    return globalCounters?.find((c) => c.counter_name === name)?.value;
  }

  return (
    <>
      <SiteHeader title="Counters" />
      <div className="flex flex-1 overflow-hidden">
        {/* Main content */}
        <div className="flex flex-col gap-4 flex-1 overflow-y-auto p-4 lg:p-6">

          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="w-full">
            <TabsList>
              <TabsTrigger value="global">Global</TabsTrigger>
              <TabsTrigger value="user">By User</TabsTrigger>
              <TabsTrigger value="aggregate">Aggregate</TabsTrigger>
            </TabsList>

            {/* ── Global ── */}
            <TabsContent value="global" className="mt-4 flex flex-col gap-4">
              <FilterInput
                value={globalFilter}
                onChange={setGlobalFilter}
                placeholder="Filter by counter name…"
              />
              {globalLoading && <SkeletonRows />}
              {globalError && <ErrorMsg error={globalErr} />}
              {!globalLoading && !globalError && (
                filteredGlobal.length > 0 ? (
                  <CounterTable>
                    {filteredGlobal.map((c) => (
                      <TableRow key={c.counter_name}>
                        <TableCell className="font-mono text-sm">{c.counter_name}</TableCell>
                        <TableCell className="text-right tabular-nums font-mono">
                          {c.value.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </CounterTable>
                ) : (
                  <Empty filter={globalFilter} fallback="No global counters found." />
                )
              )}
            </TabsContent>

            {/* ── By User ── */}
            <TabsContent value="user" className="mt-4 flex flex-col gap-4">
              {/* User ID search */}
              <form onSubmit={handleUserSubmit} className="flex items-center gap-2 max-w-lg">
                <div className="relative flex-1">
                  <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    placeholder="User ID…"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    className="pl-8 font-mono text-sm"
                  />
                </div>
                <Button type="submit" variant="outline" size="sm">
                  Search
                </Button>
              </form>

              {/* User heading */}
              {userId && (
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium">
                    {user?.Username ?? <Skeleton className="inline-block h-4 w-24 align-middle" />}
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">{userId}</span>
                </div>
              )}

              {/* Counter name filter — shown once a user is loaded */}
              {userId && !countersLoading && !countersError && userCounters && (
                <FilterInput
                  value={userFilter}
                  onChange={setUserFilter}
                  placeholder="Filter by counter name…"
                />
              )}

              {!userId && (
                <p className="text-sm text-muted-foreground">Enter a user ID above to load their counters.</p>
              )}
              {userId && countersLoading && <SkeletonRows />}
              {userId && countersError && <ErrorMsg error={countersErr} />}
              {userId && !countersLoading && !countersError && userCounters && (
                filteredUserCounters.length > 0 ? (
                  <CounterTable>
                    {filteredUserCounters.map((c) => (
                      <TableRow key={c.counter_name}>
                        <TableCell className="font-mono text-sm">{c.counter_name}</TableCell>
                        <TableCell className="text-right tabular-nums font-mono">
                          {c.value.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </CounterTable>
                ) : (
                  <Empty filter={userFilter} fallback="No counters for this user." />
                )
              )}
            </TabsContent>

            {/* ── Aggregate ── */}
            <TabsContent value="aggregate" className="mt-4 flex flex-col gap-4">
              <FilterInput
                value={aggregatePattern}
                onChange={setAggregatePattern}
                placeholder="Regex pattern… (empty = all)"
              />
              {aggregateLoading && <SkeletonRows />}
              {aggregateError && <ErrorMsg error={aggregateErr} />}
              {!aggregateLoading && !aggregateError && (
                (aggregateCounters ?? []).length > 0 ? (
                  <CounterTable valueHeader="Total">
                    {(aggregateCounters ?? []).map((c) => (
                      <TableRow key={c.counter_name}>
                        <TableCell className="font-mono text-sm">{c.counter_name}</TableCell>
                        <TableCell className="text-right tabular-nums font-mono">
                          {c.total.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </CounterTable>
                ) : (
                  <Empty filter={aggregatePattern} fallback="No aggregate counters found." />
                )
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Right sidebar */}
        <aside className="w-lg shrink-0 border-l flex flex-col sticky top-0 h-[calc(100vh-var(--header-height,3.5rem))]">
          <div className="px-4 py-3 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Counter Names
            </p>
            {counterNames && (
              <span className="text-xs text-muted-foreground">
                {sidebarNames.length}/{counterNames.length}
              </span>
            )}
          </div>
          <Separator />
          <div className="flex-1 overflow-y-auto px-2 py-2">
            {namesLoading && (
              <div className="flex flex-col gap-2 px-2 pt-1">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            )}
            {!namesLoading && sidebarNames.length === 0 && (
              <p className="px-2 text-xs text-muted-foreground">
                {activeFilter ? "No matches." : "No counters found."}
              </p>
            )}
            {sidebarNames.map((name) => {
              const value = sidebarValue(name);
              return (
                <div
                  key={name}
                  className="px-2 py-1.5 rounded-md text-xs font-mono text-foreground hover:bg-accent flex items-center justify-between gap-2"
                >
                  <span className="break-all">{name}</span>
                  {value !== undefined && (
                    <span className="tabular-nums text-muted-foreground shrink-0">
                      {value.toLocaleString()}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </>
  );
}

// ── Small local helpers ──────────────────────────────────────────────────────

function FilterInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative max-w-lg">
      <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-8 pr-8 text-sm"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <IconX className="size-3.5" />
        </button>
      )}
    </div>
  );
}

function CounterTable({
  children,
  valueHeader = "Value",
}: {
  children: React.ReactNode;
  valueHeader?: string;
}) {
  return (
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Counter</TableHead>
            <TableHead className="text-right">{valueHeader}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{children}</TableBody>
      </Table>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function ErrorMsg({ error }: { error: unknown }) {
  return (
    <p className="text-sm text-destructive">
      {(error as Error)?.message ?? "Failed to load counters."}
    </p>
  );
}

function Empty({ filter, fallback }: { filter: string; fallback: string }) {
  return (
    <p className="text-sm text-muted-foreground">
      {filter ? "No counters match that filter." : fallback}
    </p>
  );
}
